import { Box, Stack, Text, useTheme } from '@chakra-ui/react';

import { useAppSelector } from 'store/hooks';

import {
  getSuggestedServiceKey,
  getVisibleSuggestedServices,
  groupSuggestedServicesBySelection
} from 'utils/helpers/suggestedServices';

import { ServiceInterface, SuggestedService } from 'types';

import { CustomScrollBar } from 'components';
import {
  SuggestedServiceGroupHeader,
  SuggestedServiceItem
} from './components';

interface SuggestedServicesListProps {
  selectedServices: ServiceInterface[];
  selectedServiceIds: number[];
  onToggleSuggestion: (suggestion: SuggestedService) => void;
}

const SuggestedServicesList = ({
  selectedServices,
  selectedServiceIds,
  onToggleSuggestion
}: SuggestedServicesListProps) => {
  const {
    colors: {
      palette: {
        booking: {
          stepService: {
            providerList: { headerBg },
            serviceList: { serviceItem }
          }
        }
      }
    }
  } = useTheme();

  const { suggestedServices = [] } = useAppSelector((state) => state.services);

  const visibleSuggestions = getVisibleSuggestedServices(suggestedServices);
  const groups = groupSuggestedServicesBySelection(
    selectedServices,
    suggestedServices
  );
  const displayGroups = (() => {
    if (groups.length > 0) {
      return groups;
    }

    if (visibleSuggestions.length === 0) {
      return [];
    }

    return [
      {
        serviceId: selectedServices[0]?.id ?? 0,
        serviceName: selectedServices[0]?.name ?? 'Your selection',
        suggestions: visibleSuggestions
      }
    ];
  })();

  if (!displayGroups.length) {
    return null;
  }

  const hasMultipleGroups = displayGroups.length > 1;
  const lastGroupIndex = displayGroups.length - 1;

  return (
    <Stack gap='12px' w='100%'>
      <Box w='100%'>
        <Box py='15px' px='18px' bg={headerBg} borderTopRadius='4px'>
          <SuggestedServiceGroupHeader />
        </Box>

        <CustomScrollBar maxHeight='none'>
          <Stack gap={0} w='100%' mt='4px'>
            {displayGroups.map(
              ({ serviceId, serviceName, suggestions }, groupIndex) => {
                const isLastGroup = groupIndex === lastGroupIndex;

                return (
                  <Box
                    key={serviceId}
                    w='100%'
                    mt={groupIndex > 0 ? '10px' : 0}
                  >
                    {hasMultipleGroups && (
                      <Box
                        bg='gray.50'
                        py='12px'
                        px={{ base: '15px', md: '20px' }}
                        borderBottom='1px solid'
                        borderColor={serviceItem.dividerColor}
                      >
                        <SuggestedServiceGroupHeader
                          title={serviceName}
                          isSubHeader
                        />
                      </Box>
                    )}

                    {suggestions.map((suggestion, index) => {
                      const isChecked = selectedServiceIds.includes(
                        suggestion.item_id
                      );
                      const isProduct = suggestion.kind === 'P';
                      const isLastItem =
                        isLastGroup && index === suggestions.length - 1;

                      return (
                        <SuggestedServiceItem
                          key={getSuggestedServiceKey(suggestion, index)}
                          suggestion={suggestion}
                          isChecked={isChecked}
                          isDisabled={isProduct}
                          isLastItem={isLastItem}
                          onToggle={() => onToggleSuggestion(suggestion)}
                        />
                      );
                    })}
                  </Box>
                );
              }
            )}
          </Stack>
        </CustomScrollBar>
      </Box>

      <Text
        fontSize='12px'
        textAlign='center'
        color='gray.500'
        px='12px'
        lineHeight='160%'
      >
        Tap a service or class to add it to your appointment. You can skip this
        step if nothing interests you.
      </Text>
    </Stack>
  );
};

export default SuggestedServicesList;
